import React, { useState, useContext, useMemo, useEffect } from 'react';
import { ThemeProvider } from '@pixel/core';
import { createInternalTheme } from '@pixel/internal/styles';

import Typography from '@pixel/core/Typography';
import Paper from '@pixel/core/Paper';
import DataTable from '@pixel/core/DataTable';
import Link from '@pixel/core/Link';
import ListBase, { ListSearchChangeProps } from '@pixel/core/ListBase';
import Tooltip from '@pixel/core/Tooltip';

import PageHeader from '@pixel/core/PageHeader';
import LayoutRoot from '@pixel/internal/LayoutRoot';
import LayoutRootSection from '@pixel/internal/LayoutRootSection';

import CircularProgress from '@pixel/core/CircularProgress';
import Breadcrumb from '@pixel/core/Breadcrumb';
import BreadcrumbItem from '@pixel/core/BreadcrumbItem';
import { SelectChangeEvent } from '@mui/material/Select';
import ActionGroup from '@pixel/core/ActionGroup';
import { useMultipleLocalizedText } from '../../../contexts/Localization/localizationHooks';
import TableChartIcon from '@pixel/assets/icons/TableChart';

import { IDepartmentsGetParms, IDepartmentsResultData, IDepartmentsData, IDepartmentsSearchGetParms, IBreadcrumbHierarchyObject } from '../OdsTypes';
import Button from '@pixel/core/Button';
import Box from '@pixel/core/Box';
import OdsContext from '../OdsContext';
import { transformDepartmentsResult, isAvailableInBreadcrumb, updateBreadcrumb } from '../OdsTransform';
import { useConfirmation, DialogLabelSet } from '@pixel/core/ConfirmationService';
import { useSnackbar } from '@pixel/core/SnackbarService';
import axios from 'axios';
import AddDepartment from './AddDepartment';

const Departments = () => {
	const jvDepartmentTitle = {
		textOverflow: 'ellipsis',
		overflow: 'hidden',
		wordBreak: 'break-all',
		whiteSpace: 'nowrap',
		display: 'inline-block',
	}

	const overlayStyling = {
    display: 'flex',
    justifyContent: 'center',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    position: 'fixed',
    top: '0',
    left: '0',
    width: '100%',
    alignItems: 'center',
    zIndex: '1',
  }

	const {
		ODS_TAB_DEPARTMENTS,
		ODS_ALL,
		ODS_SEARCH_PLACEHOLDER,
		ODS_SEARCH_DEPARTMENTS,
		ODS_CREATED_BY,
		ODS_DATE_CREATED,
		ODS_SUB_DEPARTMENTS,
		ODS_STATUS,
		ODS_ROWS_PER_PAGE_LABEL,
		ODS_DESCRIPTION,
		I18N_NO_RESULTS_FOUND,
		ODS_ACTIONS_ADD_DEPARTMENT,
		ODS_ACTIONS_DUPLICATE,
		ODS_ACTIONS_EDIT,
		ODS_ACTIONS_MAKE_ACTIVE,
		ODS_ACTIONS_MAKE_INACTIVE,
		ODS_ACTIONS_ADD_SUB_DEPARTMENT,
		ODS_DELETE,
		ODS_PARENT_DEPARTMENT,
		ODS_MAKE_BU_ACTIVATE_SUCCESSFUL,
		ODS_DEPARTMENT_SUBSIDIARIES_INACTIVATE_SUCCESSFUL,
		ODS_MAKE_BU_INACTIVATE_SUCCESSFUL,
		ODS_UPDATE_DEPARTMENT_MODAL_TITLE,
		ODS_DEPARTMENT_UPDATE_FAILED,
		ODS_ACTIVATE_DEPARTMENT_DESCRIPTION,
		ODS_INACTIVATE_DEPARTMENT_CONFIRMATION_DESC,
		ODS_DEPARTMENT_DELETE_FAILED,
		ODS_DEPARTMENT_DELETE_SUCCESSFULLY,
		ODS_DEPARTMENT_REQ_LINK_ERROR,
		ODS_DEPARTMENT_CHILD_DEP_ERROR,
		ODS_DEPARTMENT_DELETE_CONFIRM,
		ODS_DEPARTMENT_DELETE_CONFIRM_TITLE,
	} = useMultipleLocalizedText();

	const ODS_DIMENSIONS = {
		ODS_DIMENSION_PARENT_IS_INACTIVE :'ODS_DIMENSION_PARENT_IS_INACTIVE',
		ODS_DIMENSION_HAS_REQUISITIONS : 'ODS_DIMENSION_HAS_REQUISITIONS',
	}

	const confirm = useConfirmation();
	const OrgElementsContext = useContext(OdsContext);
	const ODSPageSizeSessionStorage = () =>
		JSON.parse(sessionStorage.getItem('ODS_PageSize'))
			? JSON.parse(sessionStorage.getItem('ODS_PageSize'))
			: OrgElementsContext.defaultPageSize;

	const DepartmentsFetchParams: IDepartmentsGetParms = useMemo(
		() => ({
			pageSize: ODSPageSizeSessionStorage(),
			pageNumber: 0,
		}),
		[]
	);

	const DepartmentsSearchParams: IDepartmentsSearchGetParms = useMemo(
		() => ({
			pageSize: ODSPageSizeSessionStorage(),
			pageNumber: 0,
			keywords: '',
		}),
		[]
	);

	const snack = useSnackbar();
	const [listLoading, setListLoading] = useState(false);
	const [depFetchPayload, setDepFetchPayload] = useState<IDepartmentsGetParms>(DepartmentsFetchParams);
	const [openAddDepDrawer, setOpenAddDepDrawer] = useState(null);
	const [depSearchPayload, setDepSearchPayload] = useState<IDepartmentsSearchGetParms>(DepartmentsSearchParams);
	const [results, setResults] = useState<IDepartmentsResultData>({ nodes: [], totalCount: 0, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
	const [DepHierarchyTree, setDepHierarchyTree] = useState([{ name: 'All', eid: '' }]);
	const [perPageSize, setPerPageSize] = useState(ODSPageSizeSessionStorage());
	const [noSearchResult, setNoSearchResult] = useState(false);
	const [searchText, setSearchText] = useState('');
	const [addDepartment, setAddDepartment] = useState(null);
	const [depDrawerData, setDepDrawerData] = useState(null);
	const [editDepartment, setEditDepartment] = useState(null);
	const [duplicateDepartment, setDuplicateDepartment] = useState(null);
	const [showInProgress, setShowInProgress] = useState(false);
	const [pagingContext, setPagingContext] = useState({
		hideFirstLastActions: true,
		perPage: perPageSize,
		page: 1,
		first: 1,
		last: perPageSize,
	});

	const fetchDepartments = async (depFetchPayload: IDepartmentsGetParms) => {
		try {
			setListLoading(true);
			await axios
				.get(window.jvUrls.admin + '/action/ods/departments', {
					params: depFetchPayload,
				})
				.then(function (response) {
					setResults(transformDepartmentsResult(response.data));
					response.data.departments.length > 0 && setNoSearchResult(false);
					setPagingContext({
						hideFirstLastActions: true,
						perPage: response.data.pageSize,
						page: response.data.pageNumber + 1,
						first:
							response.data.departments.length > 0
								? response.data.pageNumber * response.data.pageSize + 1
								: 0,
						last:
							response.data.pageSize * response.data.pageNumber +
							response.data.departments.length,
					});
					setListLoading(false);
				})
				.catch(function (error) {
					setNoSearchResult(true);
					console.log(error);
					setListLoading(false);
				});
		} catch (error) {
			console.log(error);
		}
	};

	const setDepListOnActions = () => {
		if (searchText !== '') {
			setDepHierarchyTree([{ name: 'All', eid: '' }]);
			setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
		}
	}

	useEffect(() => {
		fetchDepartments(depFetchPayload);
	}, [depFetchPayload]);

	useEffect(() => {
		openAddDepDrawer!=null && fetchDepartments(depFetchPayload);
		if (!openAddDepDrawer) {
			setDepDrawerData(null);
			setAddDepartment(null);
			setEditDepartment(null);
			setDuplicateDepartment(null);
		} else {
			setSearchText('')
		}
	}, [openAddDepDrawer]);

	const editSelectedDep = (e: any, node: IDepartmentsData) => {
		try {
			setDepDrawerData(node);
			setListLoading(true);
			setEditDepartment(true);
			axios
				.get(window.jvUrls.admin + '/action/ods/departments/' + node.eid)
				.then(function (response) {
					setDepDrawerData({...response.data, subDepartmentsCount: node.subDepartmentsCount});
					setOpenAddDepDrawer(true);
				})
				.catch(function (error) {
					console.log(error);
				});
		} catch (error) {
			console.log(error);
		}
	};

	const buildSuccessSnackbarResponse = (node: IDepartmentsData, status: boolean): String => {
		let response = '';
		if (status) {
			response = ODS_MAKE_BU_ACTIVATE_SUCCESSFUL;
		} else {
			if (node.subDepartmentsCount > 0) {
				response = ODS_DEPARTMENT_SUBSIDIARIES_INACTIVATE_SUCCESSFUL;
			} else {
				response = ODS_MAKE_BU_INACTIVATE_SUCCESSFUL;
			}
		}
		return response;
	}

	const toggleDepStatus = (node: IDepartmentsData, status: boolean) => {
		try {
			axios.put(
				`${window.jvUrls.admin}/action/ods/departments/activation/${node.eid}`,
				{ active: status },
				{headers: {'Content-Type': 'application/json' }})
				.then(response => {
					setOpenAddDepDrawer(false);
					const snackbarResponse = buildSuccessSnackbarResponse(node, status);
					snack.showSuccess(`${node.name} ${snackbarResponse}`, OrgElementsContext.snackBarAutoHideTime);
					searchText === '' && updatePayload(DepHierarchyTree[DepHierarchyTree.length - 1]);
					searchText !== '' && setSearchText('');
					setShowInProgress(false);
					fetchDepartments(depFetchPayload);
				}).catch(error => {
					setShowInProgress(false);
					if (error.response.status === 400) {
						const { errorCode, message } = error.response.data;
						if (errorCode === ODS_DIMENSIONS.ODS_DIMENSION_HAS_REQUISITIONS) {
							snack.showError(message, OrgElementsContext.snackBarAutoHideTime);
						}
						if (errorCode === ODS_DIMENSIONS.ODS_DIMENSION_PARENT_IS_INACTIVE) {
							confirm.open({
								dialogLabelSet: DialogLabelSet.ALERT,
								title: ODS_UPDATE_DEPARTMENT_MODAL_TITLE,
								description: message,
								onResolve: (reason: string) => { },
								onReject: (reason: string) => { }
							});
						}
					} else {
						snack.showError(`${ODS_DEPARTMENT_UPDATE_FAILED}`, OrgElementsContext.snackBarAutoHideTime);
					}
					searchText !== '' && setSearchText('');
				})
		} catch (error) {
			snack.showError(`${ODS_DEPARTMENT_UPDATE_FAILED}`, OrgElementsContext.snackBarAutoHideTime);
		}
	}

	const makeDepartmentActive = (node: IDepartmentsData) => {
		setOpenAddDepDrawer(false);
		if (node.subDepartmentsCount > 0) {
			confirm.open({
				dialogLabelSet: DialogLabelSet.CONFIRM,
				title: ODS_UPDATE_DEPARTMENT_MODAL_TITLE,
				description: ODS_ACTIVATE_DEPARTMENT_DESCRIPTION,
				onResolve: (reason: string) => {
					setShowInProgress(true);
					toggleDepStatus(node, true);
					searchText !== '' && setSearchText('');
				},
				onReject: (reason: string) => {
					setSearchText('');
				}
			});
		} else {
			setShowInProgress(true);
			toggleDepStatus(node, true);
		}
	}

	const makeDepartmentInActive = (node: IDepartmentsData) => {
		setOpenAddDepDrawer(false);
		if (node.subDepartmentsCount > 0) {
			confirm.open({
				dialogLabelSet: DialogLabelSet.CONFIRM,
				title: ODS_UPDATE_DEPARTMENT_MODAL_TITLE,
				description: ODS_INACTIVATE_DEPARTMENT_CONFIRMATION_DESC,
				onResolve: (reason: string) => {
					setShowInProgress(true);
					toggleDepStatus(node, false);
					searchText !== '' && setSearchText('');
				},
				onReject: (reason: string) => {
					setSearchText('');
				}
			});
		} else {
			setShowInProgress(true);
			toggleDepStatus(node, false);
		}
	}

	const duplicateDepartmentAction = (e: any, node: IDepartmentsData) => {
		setDepDrawerData(node);
		editSelectedDep(e, node);
		setDuplicateDepartment(true);
	};

	const addSubDepartmentAction = (e: any, node: IDepartmentsData) => {
		setDepDrawerData(node);
		setAddDepartment(true);
    setOpenAddDepDrawer(true);
	}

	const deleteDepartment = (node: IDepartmentsData) => {
		searchText !== '' && setSearchText('');
		let selectedParentEId = DepHierarchyTree[DepHierarchyTree.length - 1].eid;
		confirm.open({
			dialogLabelSet: DialogLabelSet.CONFIRM,
			title: ODS_DEPARTMENT_DELETE_CONFIRM_TITLE,
			description: ODS_DEPARTMENT_DELETE_CONFIRM,
			onResolve: (reason: string) => {
				setShowInProgress(true);
				axios
					.delete(window.jvUrls.admin + '/action/ods/departments/' + node.eid)
					.then(function (response) {
						setShowInProgress(false);
						if ([201, 200].includes(response.status)) {
							snack.showSuccess(ODS_DEPARTMENT_DELETE_SUCCESSFULLY, OrgElementsContext.snackBarAutoHideTime);
						} else {
							snack.showError(ODS_DEPARTMENT_DELETE_FAILED, OrgElementsContext.snackBarAutoHideTime);
						}
						openAddDepDrawer && setOpenAddDepDrawer(false);
						selectedParentEId !== '' ? setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), parentEId: selectedParentEId, pageNumber: 0 }) : setDepFetchPayload({pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
					})
					.catch(function (error) {
						setShowInProgress(false);
						openAddDepDrawer && setOpenAddDepDrawer(false);
						selectedParentEId !== '' ? setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), parentEId: selectedParentEId, pageNumber: 0 }) : setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
						if(error.response.data.errorMessage.search('has children Departments') != -1){
              snack.showError(ODS_DEPARTMENT_CHILD_DEP_ERROR, OrgElementsContext.snackBarAutoHideTime);
            } else if(error.response.data.errorMessage.search('requisitions associated with it') != -1){
              snack.showError(ODS_DEPARTMENT_REQ_LINK_ERROR, OrgElementsContext.snackBarAutoHideTime);
            } else {
              snack.showError(ODS_DEPARTMENT_DELETE_FAILED, OrgElementsContext.snackBarAutoHideTime);
            }
					});
			},
			onReject: (reason: string) => {
				setSearchText('');
			}
		});
	}

	const transformActionList = (actionList: any) => {
		const depActions = [
			{ i18Label: ODS_ACTIONS_ADD_SUB_DEPARTMENT, name: 'addSubDept' },
			{ i18Label: ODS_ACTIONS_DUPLICATE, name: 'duplicate' },
			{ i18Label: ODS_ACTIONS_EDIT, name: 'edit' },
			{ i18Label: ODS_ACTIONS_MAKE_ACTIVE, name: 'makeActive' },
			{ i18Label: ODS_ACTIONS_MAKE_INACTIVE, name: 'makeInactive' },
			{ i18Label: ODS_DELETE, name: 'delete' },
		];
		const actionLinks: any = [];
		if (actionList) {
			Object.keys(actionList).forEach((key) => {
				let filteredAction = depActions.filter((e) => e.name === key);
				if (filteredAction.length > 0 && actionList[key] !== 'hidden' && actionList[key] !== null) {
					let actionTertiaryLink = {
						type: 'tertiary',
						label: filteredAction[0].i18Label,
						disabled: actionList[key] === 'grayout',
						onClick: (event: any, data: any) => {
							setDepListOnActions();
							filteredAction[0].name === 'duplicate' && duplicateDepartmentAction(event, data);
							filteredAction[0].name === 'addSubDept' && addSubDepartmentAction(event, data);
							filteredAction[0].name === 'edit' && editSelectedDep(event, data);
							filteredAction[0].name === 'makeActive' && makeDepartmentActive(data);
							filteredAction[0].name === 'makeInactive' && makeDepartmentInActive(data);
							filteredAction[0].name === 'delete' && deleteDepartment(data);
						}
					};
					actionLinks.push(actionTertiaryLink);
				}
			});
		}
		return actionLinks;
	};

	const onHandleDepClick = (departments: IBreadcrumbHierarchyObject) => {
		const newEntry = { name: departments.name, eid: departments.eid };
		setSearchText('');
		!isAvailableInBreadcrumb(DepHierarchyTree, departments) && setDepHierarchyTree((DepHierarchyTree) => [...DepHierarchyTree, newEntry]);
		setDepFetchPayload({ ...depFetchPayload, parentEId: departments.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
	}

	const onPaginationChange = (event: SelectChangeEvent) => {
		sessionStorage.setItem('ODS_PageSize', JSON.stringify(Number(event.target.value)));
		setPerPageSize(Number(event.target.value));
		if (searchText !== '') {
			setDepSearchPayload({ ...depSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(searchText) })
			setListLoading(true);
		} else {
			updatePayload(DepHierarchyTree[DepHierarchyTree.length - 1]);
		}
	}

	const onHandleSearchedDepClick = (department: IBreadcrumbHierarchyObject, DepTree: Array<IBreadcrumbHierarchyObject>) => {
		let newEntry;
		if(DepTree){
			let index = DepTree.findIndex(dep => dep.eid === department.eid);
			let DepBreadcrumb = DepTree.slice(0, index+1);
			newEntry = [{ name: 'All', eid: '' }, ...DepBreadcrumb];
		} else {
			newEntry = [{ name: 'All', eid: '' }, { name: department.name, eid: department.eid }];
		}
		setSearchText('');
		setDepHierarchyTree(newEntry);
		setDepFetchPayload({ ...depFetchPayload, parentEId: department.eid, pageNumber: 0, pageSize: ODSPageSizeSessionStorage() });
	}

	const renderParentDep = (node: IDepartmentsData) => {
		const list: any = [];
		if (node.ancestors && node.ancestors.length > 0) {
			node.ancestors.map((parent, index) => {
				list.push(
					<span style={{ display: 'inline-flex' }}>
						<Tooltip key={parent.eid} title={parent.name} arrow placement="bottom"><Link underline="hover" sx={{ ...jvDepartmentTitle, maxWidth: '160px', fontWeight: '600' }} onClick={() => onHandleSearchedDepClick({ name: parent.name, eid: parent.eid }, node.ancestors)} variant="body2">{parent.name}</Link></Tooltip>
						<span style={{ padding: '0 6px' }}>{index < node.ancestors.length - 1 && ' /   '}</span>
					</span>
				);
			})
		}
		return list;
	}

	const populateTitle = (node: any) => {
		if (node.subDepartmentsCount > 0) {
			return <Tooltip title={node.name} arrow placement='bottom'><Link underline='hover' sx={{ ...jvDepartmentTitle, fontWeight: '600', maxWidth: '100%' }} onClick={() => (searchText.length > 1) ? onHandleSearchedDepClick({ name: node.name, eid: node.eid }, node.ancestors && [...node.ancestors, {name: node.name, eid: node.eid}]) : onHandleDepClick(node)} variant='body2'>{node.name}</Link></Tooltip>;
		} else {
			return <Tooltip title={node.name} arrow placement='bottom'><span><Typography variant='body2' sx={{...jvDepartmentTitle, maxWidth: '100%' }} disabled={!node.active}>{node.name}</Typography></span></Tooltip>;
		}
	}


	const addDepartmentDrawer = (e: any, node: IDepartmentsData) => {
		setAddDepartment(true)
		setOpenAddDepDrawer(true);
	}

	const updatePayload = (departments: IBreadcrumbHierarchyObject) => {
		if (departments.eid === '') {
			setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
		} else {
			setDepFetchPayload({ ...depFetchPayload, parentEId: departments.eid, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0 });
		}
	}

	const onBreadcrumChange = (departments: IBreadcrumbHierarchyObject) => {
		setDepHierarchyTree(updateBreadcrumb(DepHierarchyTree, departments));
		updatePayload(departments);
	}

	const onPageChanged = (event: any, currPage: number) => {
		if (searchText.length > 1) {
			setDepSearchPayload({ ...depSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
		} else {
			if (DepHierarchyTree[DepHierarchyTree.length - 1].eid === '') {
				setDepFetchPayload({ pageSize: ODSPageSizeSessionStorage(), pageNumber: currPage - 1 });
			} else {
				setDepFetchPayload({ ...depFetchPayload, pageSize: ODSPageSizeSessionStorage(), parentEId: DepHierarchyTree[DepHierarchyTree.length - 1].eid, pageNumber: currPage - 1 });
			}
		}
	}

	useEffect(() => {
		if (searchText && searchText.length > 1) {
			axios.get(window.jvUrls.admin + '/action/ods/departments/search', {
				params: depSearchPayload
			})
				.then(function (response) {
					setResults(transformDepartmentsResult(response.data));
					setPagingContext({
						hideFirstLastActions: true,
						perPage: response.data.pageSize,
						page: response.data.pageNumber + 1,
						first:
							response.data.departments.length > 0
								? response.data.pageNumber * response.data.pageSize + 1
								: 0,
						last:
							response.data.pageSize * response.data.pageNumber +
							response.data.departments.length,
					});
					setListLoading(false);
					response.data.totalCount == 0 ? setNoSearchResult(true) : setNoSearchResult(false);
				})
				.catch(function (error) {
					console.log(error);
					setListLoading(false);
				});
		}
	}, [searchText, depSearchPayload]);

	const handleSearch = (props: ListSearchChangeProps) => {
		setSearchText(props.q);
		if (props.q.length > 1) {
			setDepSearchPayload({ ...depSearchPayload, pageSize: ODSPageSizeSessionStorage(), pageNumber: 0, keywords: encodeURIComponent(props.q) })
			setListLoading(true);
		} else {
			setNoSearchResult(false);
			setDepListOnActions();
		}
	};

	const theme = createInternalTheme({ darkMode: false, theme: {} });

	return (
		<React.StrictMode>
			<ThemeProvider theme={theme}>
				<LayoutRoot>
					<LayoutRootSection top bottom transparent className='jv-tab-panel-wrapper'>
						<Button key='contained' variant='contained' className='ods-header-button department-add-button' onClick={addDepartmentDrawer} color='primary'>{ODS_ACTIONS_ADD_DEPARTMENT}</Button>
						<PageHeader title={ODS_TAB_DEPARTMENTS} className={'department-header-text'} variant='subHeader' />
						<Paper elevation={2} sx={{ p: 3, mb: 3 }}>
							{(searchText.length <= 1 && <Breadcrumb variant='primaryLink' className='ods-breadcrumb department-breadcrumb' maxItems={5} itemsBeforeCollapse={2} itemsAfterCollapse={2}>
								{DepHierarchyTree.map((departments, i) => (i === DepHierarchyTree.length - 1) ? (
									<BreadcrumbItem key={i} variant label={departments.name} className={'department-breadcrumb-link'+i}/>
								) : (<BreadcrumbItem key={i} className={'department-breadcrumb-link'+i} onClick={() => onBreadcrumChange(departments)} variant label={departments.name} />))}
							</Breadcrumb>)}
							{(searchText.length > 1 && <Breadcrumb variant='primaryLink' className='ods-breadcrumb department-breadcrumb'>
								<BreadcrumbItem variant label={ODS_ALL} className='department-breadcrumb-all' onClick={() => { onBreadcrumChange({ name: ODS_ALL, eid: '' }); setSearchText('') }} />
								<BreadcrumbItem variant maxLength={60} className='department-breadcrumb-search-all' label={'Search Results for: "' + searchText + '"'} />
							</Breadcrumb>)}
						</Paper>
						{(showInProgress && <Box sx={overlayStyling}><CircularProgress size={60} /></Box>)}
						<ListBase
							className='department-list-base'
							showSearch
							labelSearch={ODS_SEARCH_PLACEHOLDER}
							labelSearchAria={ODS_SEARCH_DEPARTMENTS}
							// @ts-ignore
							results={results}
							onSearch={handleSearch}
							appliedSearchQuery={searchText}
							pagingContext={pagingContext}
							onPageChanged={onPageChanged}
							onPaginationChange={(event: SelectChangeEvent) => onPaginationChange(event)}
							rowsPerPageLabel={ODS_ROWS_PER_PAGE_LABEL}
							resultsPerPage={perPageSize}
							perPageOptions={OrgElementsContext.perPageSelectOptions}
							viewContexts={[
								{
									ariaLabel: 'Datatable View',
									icon: <TableChartIcon />,
									renderResults: (viewContextProps) => {
										if (listLoading) {
											return (<div className='department-list-loader' style={{ display: 'flex', justifyContent: 'center' }}>
												<CircularProgress size={34} />
											</div>)
										} else {
											if (!noSearchResult) {
												return (<DataTable className='department-data-table'
													{...viewContextProps}
													getRowID={node => node.id}
													columns={[
														{ id: 'Department', columnName: ODS_TAB_DEPARTMENTS, maxWidth: 240, renderCell: (node: IDepartmentsData) => populateTitle(node) },
														{ id: 'SubDepartment', columnName: ODS_SUB_DEPARTMENTS, renderCell: (node: IDepartmentsData) => <Typography variant='body2' disabled={!node.active}>{node.subDepartmentsCount}</Typography>, maxWidth: 80 },
														{ id: 'Description', columnName: ODS_DESCRIPTION, renderCell: (node: IDepartmentsData) => <Tooltip arrow title={node.description} placement='bottom'><span><Typography className='jv-business-desc' variant='body2' disabled={!node.active}>{node.description}</Typography></span></Tooltip>, maxWidth: 220 },
														...(searchText.length > 1 ? [{ id: 'ParentBusinessUnit', columnName: ODS_PARENT_DEPARTMENT, renderCell: (node: IDepartmentsData) => renderParentDep(node),  maxWidth: 220}] : []),
														{ id: 'Status', columnName: ODS_STATUS, renderCell: (node: IDepartmentsData) => (node.active) ? <Typography variant='body2' color='success' sx={{ color: 'green' }}>Active</Typography> : <Typography variant='body2' color='error' className='error-text-style' disabled>Inactive</Typography>, maxWidth: 48 },
													]}
													renderActions={(node: IDepartmentsData) => {
														return (<ActionGroup id='primary-buttons-actions' actions={transformActionList(node.actions)} data={node} />)
													}}
												/>)
											} else {
												return (<div className={'dep-list-no-results'}><Paper sx={{ display: 'flex', justifyContent: 'center', padding: '16px' }}> {I18N_NO_RESULTS_FOUND}</Paper></div>)
											}
										}
									}
								}
							]}
						/>
					</LayoutRootSection>
				</LayoutRoot>
				<AddDepartment
					addDrawerState={openAddDepDrawer}
					setaddDrawerState={setOpenAddDepDrawer}
					addDepartment={addDepartment}
					editDepartment={editDepartment}
					depDrawerData={depDrawerData}
					duplicateDepartment={duplicateDepartment}
					setDuplicateDepartment = {setDuplicateDepartment}
					makeDepartmentActive={makeDepartmentActive}
					makeDepartmentInActive={makeDepartmentInActive}
					deleteDepartment={deleteDepartment}
				/>
			</ThemeProvider>
		</React.StrictMode>
	);
}

export default Departments;